import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from 'reactstrap';
import API from '@beardeddevops/react.api';
import Types from 'constants/Types';
import {Objects} from 'components';
import Framework from '@beardeddevops/react.framework';
import moment from 'moment-timezone';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import {goToHelpdeskContacted, notifyHelpdeskContactedUpdated} from 'components/Header/Notifications';
import {
	applyHelpdeskCallSnooze,
	buildHelpdeskCallQueue,
	clearHelpdeskCallSnooze,
	getCallRowKey,
	getEngineerName,
	getEngineerPkey,
	getLastContacted,
	getLastContactedLabel,
	readHelpdeskCallSnoozeMap,
	seesHelpdeskCallBanner,
	writeHelpdeskCallSnoozeMap,
} from './helpdeskCallQueue';

export {HELPDESK_CALL_FE_TYPE, seesHelpdeskCallBanner} from './helpdeskCallQueue';

const SNOOZE_MINUTES_NAME = 'helpdesk_call_banner_snooze_minutes';
const DEFAULT_SNOOZE_MINUTES = 30;

/**
 * Global stacked banner for overdue FE call notifications (type 9).
 * Shown when the viewer has the Helpdesk role and main_role is greater than Executive.
 * Soft-dismiss snoozes locally; permanent clear happens when a contact is logged.
 */
const HelpdeskCallBanner = ({notifications = [], history, onSessionCheck, roles, mainRole}) => {
	const [snoozeMap, setSnoozeMap] = useState(() => readHelpdeskCallSnoozeMap());
	const [now, setNow] = useState(() => Date.now());
	const [snoozeMinutes, setSnoozeMinutes] = useState(DEFAULT_SNOOZE_MINUTES);
	const [logCallOpen, setLogCallOpen] = useState(false);
	const [activeEngineer, setActiveEngineer] = useState(null);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const model = new API.BuildClass(Types.Count_Variables._name, API.RequestType.READ);
				model.filter = {names: [SNOOZE_MINUTES_NAME]};
				const response = await model.submit(null, 'GET_ALL');
				const amount = response?.by_name?.[SNOOZE_MINUTES_NAME]?.amount;
				if (!cancelled && amount != null && Number(amount) > 0) {
					setSnoozeMinutes(Number(amount));
				}
			} catch (e) {
				// keep default
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			setNow(Date.now());
			setSnoozeMap(readHelpdeskCallSnoozeMap());
		}, 30000);
		return () => clearInterval(interval);
	}, []);

	const queue = useMemo(() => {
		return applyHelpdeskCallSnooze(buildHelpdeskCallQueue(notifications), snoozeMap, now);
	}, [notifications, snoozeMap, now]);

	useEffect(() => {
		if (queue.length <= 1) {
			setExpanded(false);
		}
	}, [queue.length]);

	const current = queue[0] ?? null;
	const remaining = Math.max(queue.length - 1, 0);

	const handleSnooze = useCallback((event, notification) => {
		event?.stopPropagation();
		const target = notification ?? current;
		if (!target) {
			return;
		}
		const engineerPkey = getEngineerPkey(target);
		if (engineerPkey == null) {
			return;
		}
		const nextMap = {
			...readHelpdeskCallSnoozeMap(),
			[String(engineerPkey)]: Date.now() + (snoozeMinutes * 60 * 1000),
		};
		writeHelpdeskCallSnoozeMap(nextMap);
		setSnoozeMap(nextMap);
		setNow(Date.now());
	}, [current, snoozeMinutes]);

	const handleOpenLogCall = useCallback((event, notification) => {
		event?.preventDefault();
		event?.stopPropagation();
		const target = notification ?? current;
		if (!target) {
			return;
		}
		const engineerPkey = getEngineerPkey(target);
		if (engineerPkey == null || Number.isNaN(engineerPkey)) {
			return;
		}
		setActiveEngineer({
			engineer_pkey: engineerPkey,
			engineer_name: getEngineerName(target),
			last_contacted: getLastContacted(target),
			notes: '',
		});
		setLogCallOpen(true);
	}, [current]);

	const handleNavigate = useCallback((event, notification) => {
		event?.stopPropagation();
		const target = notification ?? current;
		if (!target) {
			return;
		}
		const engineerPkey = getEngineerPkey(target);
		goToHelpdeskContacted(history, engineerPkey);
	}, [current, history]);

	const handleToggleExpanded = useCallback((event) => {
		event?.stopPropagation();
		event?.preventDefault();
		setExpanded((prev) => !prev);
	}, []);

	const handleCloseLogCall = useCallback(() => {
		setLogCallOpen(false);
		setActiveEngineer(null);
	}, []);

	const handleAfterSave = useCallback(() => {
		if (activeEngineer?.engineer_pkey != null) {
			setSnoozeMap(clearHelpdeskCallSnooze(activeEngineer.engineer_pkey));
		}
		handleCloseLogCall();
		notifyHelpdeskContactedUpdated(activeEngineer?.engineer_pkey);
		if (typeof onSessionCheck === 'function') {
			setTimeout(() => onSessionCheck(), 500);
		}
	}, [activeEngineer, handleCloseLogCall, onSessionCheck]);

	const renderRow = (notification, {showExpand} = {}) => {
		const engineerName = getEngineerName(notification);
		const lastContactedLabel = getLastContactedLabel(notification);
		const label = engineerName.startsWith('Call ') ? engineerName : `Call ${engineerName}`;

		return (
			<div
				className="helpdesk-call-banner-row"
				role="button"
				tabIndex={0}
				onClick={(event) => handleNavigate(event, notification)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						handleNavigate(event, notification);
					}
				}}
			>
				<div className="helpdesk-call-banner-copy">
					<strong>{label}</strong>
					{lastContactedLabel ? <span className="helpdesk-call-banner-meta">{lastContactedLabel}</span> : null}
					{showExpand && remaining > 0 && (
						<button
							type="button"
							className="helpdesk-call-banner-more"
							onClick={handleToggleExpanded}
							aria-expanded={expanded}
							aria-label={expanded ? 'Collapse call list' : `Show ${remaining} more calls`}
						>
							<span>(+{remaining} more)</span>
							{expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
						</button>
					)}
				</div>
				<div className="helpdesk-call-banner-actions">
					<Button size="sm" color="light" outline onClick={(event) => handleSnooze(event, notification)}>
						Dismiss
					</Button>
					<Button size="sm" color="light" onClick={(event) => handleOpenLogCall(event, notification)}>
						Log Call
					</Button>
				</div>
			</div>
		);
	};

	if (!seesHelpdeskCallBanner(roles, mainRole) || !current) {
		return null;
	}

	return (
		<>
			<div className={`helpdesk-call-banner${expanded ? ' is-expanded' : ''}`}>
				{renderRow(current, {showExpand: true})}
				{expanded && remaining > 0 && (
					<div className="helpdesk-call-banner-list">
						{queue.slice(1).map((notification, index) => (
							<React.Fragment key={getCallRowKey(notification, index + 1)}>
								{renderRow(notification)}
							</React.Fragment>
						))}
					</div>
				)}
			</div>

			<Framework.Elements.UniversalModal
				name={`Log Call - ${activeEngineer?.engineer_name ?? 'Field Engineer'}`}
				object={Objects.HelpDesksContacted}
				history={history}
				toggle={logCallOpen}
				reportClose={handleCloseLogCall}
				afterSave={handleAfterSave}
				objectProps={{
					current_note: activeEngineer ?? {engineer_pkey: null, notes: ''},
					date_contacted: moment.tz('America/Phoenix').format('YYYY-MM-DD'),
				}}
				hideGoTo={true}
				modalProps={{zIndex: 1400}}
			/>
		</>
	);
};

export default HelpdeskCallBanner;
